"use client";

import React from "react";
import { Activity, ArrowRight, ArrowDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Vo2MetabolicHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true,
    threshold: 0.1
  });
  return <section id="vo2metabolichero" data-nav="light" ref={ref} className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28 transition-opacity duration-700 isolate" style={{
    opacity: isVisible ? 1 : 0
  }}>
      {/* Subtle scientific ambient grid texture */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--color-primary)_0%,_transparent_55%)] opacity-5" aria-hidden="true" />
      <div className="pointer-events-none absolute right-0 top-1/4 -z-10 h-96 w-96 rounded-full bg-accent/30 blur-3xl" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Clinical & Assessment Messaging */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <Badge variant="outline" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/25 bg-primary/5">
              <Activity className="size-3.5 text-primary" />
              VO₂ Max &amp; Metabolic Testing
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-sans font-bold tracking-tight text-foreground leading-[1.1]">
              Understand your fitness. Understand your metabolism. Train with real data.
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              PhysAnalytica uses the KORR CardioCoach MAX to objectively measure aerobic fitness and resting metabolism, providing personalised information that can help inform training, nutrition, and progress monitoring.
            </p>

            <p className="text-sm sm:text-base font-medium text-foreground/80 border-l-2 border-primary/40 pl-4 py-0.5">
              For everyday exercisers, people working on their health and fitness, and competitive athletes.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto px-7 shadow-xs">
                <Link to="#">
                  Book or Enquire
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6">
                <Link to="#vo2-pricing">
                  View Assessment Options
                  <ArrowDown className="size-4 ml-1.5" />
                </Link>
              </Button>
            </div>

            {/* Trust line */}
            <div className="pt-6 border-t border-border w-full">
              <p className="text-xs sm:text-sm font-medium tracking-wide uppercase text-muted-foreground flex flex-wrap items-center gap-2 sm:gap-3">
                <span>VO₂ Max</span>
                <span className="text-primary font-bold" aria-hidden="true">•</span>
                <span>Resting Metabolic Rate</span>
                <span className="text-primary font-bold" aria-hidden="true">•</span>
                <span>Personalised Exercise Data</span>
              </p>
            </div>
          </div>

          {/* Right Column: Authentic Exercise & Mask-based Testing Visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/metabolic_vo2max_testing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/metabolic_vo2max_testing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/metabolic_vo2max_testing.png-wvc-sizes" alt="Athletic VO2 max and metabolic rate testing with metabolic mask and metabolic cart monitoring" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-4/3 sm:aspect-5/4 lg:aspect-4/5" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" aria-hidden="true" />
              </div>

              {/* Decorative accent element */}
              <div className="absolute -bottom-4 -left-4 -z-10 h-full w-full rounded-2xl border border-primary/20 bg-primary/5" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}